import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Frame, Loader2 } from 'lucide-react';
import { useUserStore } from '@/lib/user-store';
import { useAdminStore } from '@/lib/admin-store';
import { useTranslation } from '@/hooks/useTranslation';
import toast from 'react-hot-toast';
import type { Artwork } from '@/types';

const INPUT_CLS = "w-full px-4 py-3 bg-black/[0.03] dark:bg-white/[0.05] border border-black/[0.06] dark:border-white/[0.08] rounded-xl text-sm font-medium outline-none focus:border-black/20 dark:focus:border-white/20 transition-all placeholder:text-black/20 dark:placeholder:text-white/20";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  artwork: Artwork;
}

function generateId(): string {
  return Math.random().toString(36).slice(2, 10);
}

const CustomFrameModal: React.FC<Props> = ({ isOpen, onClose, artwork }) => {
  const { t, localize } = useTranslation();
  const currentUser = useUserStore((s) => s.currentUser);
  const addConversation = useAdminStore((s) => s.addConversation);
  const language = t.nav.home === 'Home' ? 'en' : 'ka';

  const [name, setName] = useState(currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : '');
  const [email, setEmail] = useState(currentUser?.email || '');
  const [phone, setPhone] = useState(currentUser?.phone || '');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const artworkTitle = localize(artwork.title);
  const artworkId = artwork._id;

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error(language === 'ka' ? 'სახელი და ელ.ფოსტა სავალდებულოა' : 'Name and email are required');
      return;
    }

    if (sending) return;
    setSending(true);
    try {
    const now = new Date().toISOString();

    const artworkUrl = `${window.location.origin}/artwork/${artwork.slug}`;
    const fullMessage = [
      `🖼 Custom Frame Request`,
      `Artwork: ${artworkTitle}`,
      `Link: ${artworkUrl}`,
      phone ? `Phone: ${phone}` : '',
      `---`,
      message.trim() || (language === 'ka' ? 'მინდა ინდივიდუალური ჩარჩო ამ ნამუშევრისთვის.' : 'I would like a custom frame for this artwork.'),
    ].filter(Boolean).join('\n');

    const firstMessage = {
      _id: generateId(),
      conversationId: '',
      senderType: currentUser ? 'user' as const : 'guest' as const,
      senderName: name.trim(),
      senderEmail: email.trim(),
      content: fullMessage,
      read: false,
      createdAt: now,
    };

    await addConversation({
      userId: currentUser?._id,
      guestName: name.trim(),
      guestEmail: email.trim(),
      subject: `Custom Frame — ${artworkTitle}`,
      source: 'custom-frame',
      messages: [firstMessage],
      status: 'open',
      lastMessageAt: now,
    });

    toast.success(language === 'ka' ? 'მოთხოვნა გაგზავნილია!' : 'Request sent!');
    setSending(false);
    setMessage('');
    onClose();
    } catch { toast.error(language === 'ka' ? 'გაგზავნა ვერ მოხერხდა. სცადეთ ხელახლა.' : 'Could not send. Please try again.'); }
    finally { setSending(false); }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[900] bg-black/30 dark:bg-black/50 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-[950] flex items-center justify-center px-4 pointer-events-none"
          >
            <div className="bg-white dark:bg-[#111] rounded-2xl w-full max-w-lg shadow-2xl pointer-events-auto border border-black/5 dark:border-white/5 overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-black/5 dark:border-white/5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-black/5 dark:bg-white/5 flex items-center justify-center">
                    <Frame size={18} className="text-black/50 dark:text-white/50" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold uppercase tracking-wider">
                      {language === 'ka' ? 'ინდივიდუალური ჩარჩო' : 'Custom Frame'}
                    </h3>
                    <p className="text-[10px] text-black/40 dark:text-white/40 truncate max-w-[250px]">
                      {artworkTitle}
                    </p>
                  </div>
                </div>
                <button onClick={onClose} className="w-8 h-8 rounded-full bg-black/5 dark:bg-white/5 flex items-center justify-center hover:bg-black/10 dark:hover:bg-white/10 transition-colors">
                  <X size={16} />
                </button>
              </div>

              {/* Artwork preview */}
              <div className="px-6 pt-4 flex items-center gap-3">
                {artwork.images?.[0] && (
                  <img src={artwork.images?.[0] || ''} alt="" className="w-14 h-14 rounded-lg object-cover shrink-0" />
                )}
                <div className="min-w-0">
                  <p className="text-xs font-bold truncate">{artworkTitle}</p>
                  <p className="text-[10px] text-black/40 dark:text-white/40">
                    {typeof artwork.dimensions === 'string' ? artwork.dimensions : artwork.dimensions ? `${(artwork.dimensions as {width?:number;height?:number;unit?:string}).width || ''}×${(artwork.dimensions as {width?:number;height?:number;unit?:string}).height || ''} ${(artwork.dimensions as {width?:number;height?:number;unit?:string}).unit || 'cm'}` : ''}
                  </p>
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[9px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40 mb-1.5">
                      {language === 'ka' ? 'სახელი' : 'Name'} *
                    </label>
                    <input value={name} onChange={(e) => setName(e.target.value)} className={INPUT_CLS} required />
                  </div>
                  <div>
                    <label className="block text-[9px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40 mb-1.5">
                      {language === 'ka' ? 'ტელეფონი' : 'Phone'}
                    </label>
                    <input value={phone} onChange={(e) => setPhone(e.target.value)} className={INPUT_CLS} placeholder="+995..." />
                  </div>
                </div>
                <div>
                  <label className="block text-[9px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40 mb-1.5">
                    {language === 'ka' ? 'ელ.ფოსტა' : 'Email'} *
                  </label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={INPUT_CLS} required />
                </div>
                <div>
                  <label className="block text-[9px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40 mb-1.5">
                    {language === 'ka' ? 'შეტყობინება' : 'Message'}
                  </label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className={INPUT_CLS + ' resize-none min-h-[80px]'}
                    rows={3}
                    placeholder={language === 'ka' ? 'დამატებითი დეტალები ჩარჩოს შესახებ...' : 'Additional details about the frame...'}
                  />
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  className="w-full flex items-center justify-center gap-2 py-3.5 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.2em] rounded-xl hover:opacity-90 disabled:opacity-50 transition-all"
                >
                  {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  {language === 'ka' ? 'მოთხოვნის გაგზავნა' : 'Send Request'}
                </button>
              </form>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CustomFrameModal;
